/**
 * IndexedDB Helper Utility
 * Provides centralized functions for common IndexedDB operations
 */

/**
 * Initialize an IndexedDB database
 * @param {string} dbName - The name of the database
 * @param {number} dbVersion - The version of the database
 * @param {string} storeName - The name of the object store to create
 * @param {Object} stateRef - Reference to a state object with dbInitialized flag
 * @returns {Promise} - Promise that resolves when the database is initialized
 */
export const initDatabase = (dbName, dbVersion, storeName, stateRef) => {
  return new Promise((resolve, reject) => {
    // Environment / availability guard
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      if (stateRef) stateRef.dbInitialized = false
      return resolve()
    }
    // Always verify the store exists; don't rely solely on a boolean flag
    let request
    try {
      // Open WITHOUT forcing a specific version to avoid VersionError (passing a lower version)
      request = indexedDB.open(dbName)
    } catch (e) {
      console.error(`[IndexedDB] Exception opening ${dbName}`, e)
      if (stateRef) stateRef.dbInitialized = false
      return resolve() // Fail soft – treat as non‑persistent
    }

    request.onerror = (event) => {
      const err = request.error
      const name = err?.name
      // Known non-fatal issues (private mode, blocked, version mismatch)
      if (
        name === 'QuotaExceededError' ||
        name === 'SecurityError' ||
        name === 'InvalidStateError' ||
        name === 'VersionError'
      ) {
        console.warn(
          `[IndexedDB] Non-fatal open error for ${dbName}: ${name}; disabling persistence`
        )
        if (stateRef) stateRef.dbInitialized = false
        return resolve()
      }
      console.error(`Error opening IndexedDB for ${dbName}`, event, err)
      reject(new Error('Failed to open database'))
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result
      // Create the object store if missing
      if (!db.objectStoreNames.contains(storeName)) {
        const store = db.createObjectStore(storeName, { keyPath: 'cacheKey' })
        store.createIndex('index', 'index', { unique: false })
        store.createIndex('version', 'version', { unique: false })
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }

    request.onsuccess = (event) => {
      const db = event.target.result

      const ensureStore = () => {
        // If store exists, we're done
        if (db.objectStoreNames.contains(storeName)) {
          try {
            db.close()
          } catch {
            /* noop */
          }
          if (stateRef) stateRef.dbInitialized = true
          resolve()
          return
        }

        // Store missing: bump version to trigger upgrade and create it
        const newVersion = (db.version || dbVersion || 1) + 1
        try {
          db.close()
        } catch {
          /* noop */
        }
        const upgradeReq = indexedDB.open(dbName, newVersion)
        upgradeReq.onupgradeneeded = (ev) => {
          const udb = ev.target.result
          if (!udb.objectStoreNames.contains(storeName)) {
            const store = udb.createObjectStore(storeName, { keyPath: 'cacheKey' })
            store.createIndex('index', 'index', { unique: false })
            store.createIndex('version', 'version', { unique: false })
            store.createIndex('timestamp', 'timestamp', { unique: false })
          }
        }
        upgradeReq.onerror = (e) => {
          console.error(`Error upgrading IndexedDB for ${dbName}`, e)
          reject(new Error('Failed to upgrade database'))
        }
        upgradeReq.onsuccess = () => {
          try {
            upgradeReq.result.close()
          } catch {
            /* noop */
          }
          if (stateRef) stateRef.dbInitialized = true
          resolve()
        }
      }

      // Proceed to ensure the store exists
      ensureStore()
    }
  })
}

/**
 * Sanitize data for IndexedDB storage
 * Removes circular references and non-serializable content
 * @param {any} data - The data to sanitize
 * @returns {any} - Clean data safe for IndexedDB
 */
export const sanitizeForDb = (data) => {
  if (!data) return data

  // For primitive types, return as is
  if (typeof data !== 'object' || data === null) {
    return data
  }

  // Handle arrays recursively
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForDb(item))
  }

  // For regular objects, create a clean copy without problematic properties
  const cleanObj = {}

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      try {
        // Skip functions, symbols, and other non-serializable types
        const val = data[key]
        const type = typeof val

        if (type === 'function' || type === 'symbol' || val === undefined) {
          continue
        }

        // Test if this property can be serialized with JSON
        // This also handles circular references
        JSON.stringify(val)

        // If we get here, the value is serializable
        cleanObj[key] = sanitizeForDb(val)
      } catch {
        // If we can't serialize it, skip this property
        console.warn(`Skipping non-serializable property '${key}' during IndexedDB storage`)
      }
    }
  }

  return cleanObj
}

// Helper to detect IndexedDB availability in the current environment
const isIndexedDBAvailable = () => typeof window !== 'undefined' && typeof indexedDB !== 'undefined'

// Track which databases we've warned about to avoid spamming the test logs
const warnedDbs = new Set()

/**
 * Save data to IndexedDB
 * @param {string} dbName - The name of the database
 * @param {string} storeName - The name of the object store
 * @param {string} cacheKey - The unique key for the cache entry
 * @param {string} index - The index for the entry (e.g., 'collection' or specific ID)
 * @param {string} version - The version identifier
 * @param {any} data - The data to store
 * @returns {Promise} - Promise that resolves when the data is saved
 */
export const saveToDb = (dbName, storeName, cacheKey, index, version, data) => {
  if (!isIndexedDBAvailable()) {
    if (!warnedDbs.has(dbName)) {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      warnedDbs.add(dbName)
    }
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName)

    const runPut = (db) => {
      const tx = db.transaction([storeName], 'readwrite')
      const store = tx.objectStore(storeName)

      const sanitizedData = sanitizeForDb(data)
      const entry = { cacheKey, index, version, timestamp: Date.now(), data: sanitizedData }

      const putRequest = store.put(entry)
      putRequest.onsuccess = () => resolve()
      putRequest.onerror = (event) => {
        console.error(`Error saving data to IndexedDB for ${dbName}/${storeName}`, event)
        reject(new Error('Failed to save data'))
      }
      tx.oncomplete = () => {
        try {
          db.close()
        } catch {
          /* noop */
        }
      }
    }

    request.onsuccess = (event) => {
      const db = event.target.result
      if (db.objectStoreNames.contains(storeName)) {
        return runPut(db)
      }
      // Store missing: upgrade and create it
      const newVersion = (db.version || 1) + 1
      try {
        db.close()
      } catch {
        /* noop */
      }
      const upgradeReq = indexedDB.open(dbName, newVersion)
      upgradeReq.onupgradeneeded = (ev) => {
        const udb = ev.target.result
        if (!udb.objectStoreNames.contains(storeName)) {
          const store = udb.createObjectStore(storeName, { keyPath: 'cacheKey' })
          store.createIndex('index', 'index', { unique: false })
          store.createIndex('version', 'version', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
      upgradeReq.onerror = (e) => {
        console.error(`Error upgrading IndexedDB for ${dbName}`, e)
        reject(new Error('Failed to upgrade database'))
      }
      upgradeReq.onsuccess = (e2) => runPut(e2.target.result)
    }

    request.onerror = (event) => {
      console.error(`Error opening IndexedDB for ${dbName}`, event)
      reject(new Error('Failed to open database'))
    }
  })
}

/**
 * Retrieve data from IndexedDB
 * @param {string} dbName - The name of the database
 * @param {string} storeName - The name of the object store
 * @param {string} cacheKey - The unique key for the cache entry
 * @returns {Promise} - Promise that resolves with the retrieved data or null if not found
 */
export const getFromDb = (dbName, storeName, cacheKey) => {
  if (!isIndexedDBAvailable()) {
    if (!warnedDbs.has(dbName)) {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      warnedDbs.add(dbName)
    }
    return Promise.resolve(null)
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName)

    const runGet = (db) => {
      const tx = db.transaction([storeName], 'readonly')
      const store = tx.objectStore(storeName)
      const getRequest = store.get(cacheKey)
      getRequest.onsuccess = (event) => {
        const result = event.target.result
        // For backwards compatibility this returns only the stored data payload
        resolve(result ? result.data : null)
      }
      getRequest.onerror = (event) => {
        console.error(`Error getting data from IndexedDB for ${dbName}/${storeName}`, event)
        reject(new Error('Failed to retrieve data'))
      }
      tx.oncomplete = () => {
        try {
          db.close()
        } catch {
          /* noop */
        }
      }
    }

    request.onsuccess = (event) => {
      const db = event.target.result
      if (db.objectStoreNames.contains(storeName)) {
        return runGet(db)
      }
      // Store missing: upgrade and create it
      const newVersion = (db.version || 1) + 1
      try {
        db.close()
      } catch {
        /* noop */
      }
      const upgradeReq = indexedDB.open(dbName, newVersion)
      upgradeReq.onupgradeneeded = (ev) => {
        const udb = ev.target.result
        if (!udb.objectStoreNames.contains(storeName)) {
          const store = udb.createObjectStore(storeName, { keyPath: 'cacheKey' })
          store.createIndex('index', 'index', { unique: false })
          store.createIndex('version', 'version', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
      upgradeReq.onerror = (e) => {
        console.error(`Error upgrading IndexedDB for ${dbName}`, e)
        reject(new Error('Failed to upgrade database'))
      }
      upgradeReq.onsuccess = (e2) => runGet(e2.target.result)
    }

    request.onerror = (event) => {
      console.error(`Error opening IndexedDB for ${dbName}`, event)
      reject(new Error('Failed to open database'))
    }
  })
}

/**
 * Retrieve the full entry (including metadata like timestamp) from IndexedDB
 * @param {string} dbName
 * @param {string} storeName
 * @param {string} cacheKey
 * @returns {Promise<Object|null>} - Resolves with the full entry or null
 */
export const getEntry = (dbName, storeName, cacheKey) => {
  if (!isIndexedDBAvailable()) {
    if (!warnedDbs.has(dbName)) {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      warnedDbs.add(dbName)
    }
    return Promise.resolve(null)
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName)

    const runGet = (db) => {
      const tx = db.transaction([storeName], 'readonly')
      const store = tx.objectStore(storeName)
      const getRequest = store.get(cacheKey)
      getRequest.onsuccess = (event) => {
        const result = event.target.result
        resolve(result || null)
      }
      getRequest.onerror = (event) => {
        console.error(`Error getting entry from IndexedDB for ${dbName}/${storeName}`, event)
        reject(new Error('Failed to retrieve entry'))
      }
      tx.oncomplete = () => {
        try {
          db.close()
        } catch {
          /* noop */
        }
      }
    }

    request.onsuccess = (event) => {
      const db = event.target.result
      if (db.objectStoreNames.contains(storeName)) {
        return runGet(db)
      }
      const newVersion = (db.version || 1) + 1
      try {
        db.close()
      } catch {
        /* noop */
      }
      const upgradeReq = indexedDB.open(dbName, newVersion)
      upgradeReq.onupgradeneeded = (ev) => {
        const udb = ev.target.result
        if (!udb.objectStoreNames.contains(storeName)) {
          const store = udb.createObjectStore(storeName, { keyPath: 'cacheKey' })
          store.createIndex('index', 'index', { unique: false })
          store.createIndex('version', 'version', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
      upgradeReq.onerror = (e) => {
        console.error(`Error upgrading IndexedDB for ${dbName}`, e)
        reject(new Error('Failed to upgrade database'))
      }
      upgradeReq.onsuccess = (e2) => runGet(e2.target.result)
    }

    request.onerror = (event) => {
      console.error(`Error opening IndexedDB for ${dbName}`, event)
      reject(new Error('Failed to open database'))
    }
  })
}

/**
 * Retrieve all entries from an IndexedDB object store filtered by an index
 * @param {string} dbName - The name of the database
 * @param {string} storeName - The name of the object store
 * @param {string} indexName - The index to filter on (e.g., 'version')
 * @param {any} indexValue - The value to match in the index
 * @returns {Promise<Array>} - Resolves with an array of entry objects { cacheKey, index, version, timestamp, data }
 */
export const getAllByIndex = (dbName, storeName, indexName, indexValue) => {
  if (!isIndexedDBAvailable()) {
    if (!warnedDbs.has(dbName)) {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      warnedDbs.add(dbName)
    }
    return Promise.resolve([])
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName)

    request.onsuccess = (event) => {
      const db = event.target.result
      const tx = db.transaction([storeName], 'readonly')
      const store = tx.objectStore(storeName)

      let idx
      try {
        idx = store.index(indexName)
      } catch (e) {
        console.error(`Index '${indexName}' not found on store '${storeName}' in '${dbName}'`, e)
        db.close()
        reject(new Error('Index not found'))
        return
      }

      const range = IDBKeyRange.only(indexValue)
      const getAllReq = idx.getAll(range)

      getAllReq.onsuccess = (ev) => {
        resolve(ev.target.result || [])
      }

      getAllReq.onerror = (ev) => {
        console.error(`Error querying index '${indexName}' on ${dbName}/${storeName}`, ev)
        reject(new Error('Failed to query by index'))
      }

      tx.oncomplete = () => {
        db.close()
      }
    }

    request.onerror = (event) => {
      console.error(`Error opening IndexedDB for ${dbName}`, event)
      reject(new Error('Failed to open database'))
    }
  })
}

/**
 * Clear all data from an IndexedDB store
 * @param {string} dbName - The name of the database
 * @param {string} storeName - The name of the object store to clear
 * @returns {Promise} - Promise that resolves when the store is cleared
 */
export const clearDbStore = (dbName, storeName) => {
  if (!isIndexedDBAvailable()) {
    if (!warnedDbs.has(dbName)) {
      console.warn(
        `[IndexedDB] Unsupported environment for ${dbName}; continuing without persistence`
      )
      warnedDbs.add(dbName)
    }
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName)

    const runClear = (db) => {
      const tx = db.transaction([storeName], 'readwrite')
      const store = tx.objectStore(storeName)
      const clearRequest = store.clear()
      clearRequest.onsuccess = () => resolve()
      clearRequest.onerror = (event) => {
        console.error(`Error clearing data from IndexedDB for ${dbName}/${storeName}`, event)
        reject(new Error('Failed to clear data'))
      }
      tx.oncomplete = () => {
        try {
          db.close()
        } catch {
          /* noop */
        }
      }
    }

    request.onsuccess = (event) => {
      const db = event.target.result
      if (db.objectStoreNames.contains(storeName)) {
        return runClear(db)
      }
      // Store missing: upgrade and create it
      const newVersion = (db.version || 1) + 1
      try {
        db.close()
      } catch {
        /* noop */
      }
      const upgradeReq = indexedDB.open(dbName, newVersion)
      upgradeReq.onupgradeneeded = (ev) => {
        const udb = ev.target.result
        if (!udb.objectStoreNames.contains(storeName)) {
          const store = udb.createObjectStore(storeName, { keyPath: 'cacheKey' })
          store.createIndex('index', 'index', { unique: false })
          store.createIndex('version', 'version', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
      upgradeReq.onerror = (e) => {
        console.error(`Error upgrading IndexedDB for ${dbName}`, e)
        reject(new Error('Failed to upgrade database'))
      }
      upgradeReq.onsuccess = (e2) => runClear(e2.target.result)
    }

    request.onerror = (event) => {
      console.error(`Error opening IndexedDB for ${dbName}`, event)
      reject(new Error('Failed to open database'))
    }
  })
}
